import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { FIRM, OFFICES, LOCAL_PARTNERS } from "@/data/site";
import { useLang } from "@/i18n/LangContext";
import Logo from "@/components/brand/Logo";

export default function Footer() {
  const { t } = useLang();
  return (
    <footer data-testid={IDS.footer.root} className="bg-[#050505] border-t border-[#141414] mt-32">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-24 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <div className="eyebrow">{t.footer.eyebrow}</div>
            <div className="mt-8">
              <h3 className="font-serif text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                {t.footer.title1}<br /><span className="italic-accent">{t.footer.title2}</span> {t.footer.title3}
              </h3>
              <p className="mt-8 text-[#A19D94] max-w-md leading-relaxed">{t.footer.lead}</p>
            </div>
            <Link to="/contact" className="btn-ghost mt-10 inline-flex" data-testid="footer-cta">
              {t.footer.cta}
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </Link>
          </div>

          <div className="lg:col-span-3">
            <div className="eyebrow mb-6">{t.footer.practice}</div>
            <ul className="space-y-3 text-[#A19D94]">
              <li><Link to="/practice-areas/cyber-crime-lawyer-dubai" className="link-hover">Cyber Crime & Digital Fraud</Link></li>
              <li><Link to="/practice-areas/economic-offences-lawyer-dubai" className="link-hover">Economic Offences</Link></li>
              <li><Link to="/practice-areas/cfd-forex-trading-dispute-lawyer-dubai" className="link-hover">CFD & Trading Disputes</Link></li>
              <li><Link to="/practice-areas/virtual-assets-vara-lawyer-dubai" className="link-hover">Virtual Assets · VARA</Link></li>
              <li><Link to="/practice-areas/data-protection-privacy-lawyer-uae" className="link-hover">Data Privacy & Cyber</Link></li>
              <li><Link to="/practice-areas/ai-governance-lawyer-uae" className="link-hover">AI Governance</Link></li>
              <li><Link to="/practice-areas/corporate-commercial-lawyer-dubai" className="link-hover">Corporate & Commercial</Link></li>
              <li><Link to="/practice-areas/india-entry-cross-border-lawyer" className="link-hover">India Entry & Cross-Border</Link></li>
              <li><Link to="/matters" className="link-hover">Selected Matters</Link></li>
              <li><Link to="/careers" className="link-hover">Careers</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <div className="eyebrow mb-6">{t.footer.offices}</div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-6">
              {OFFICES.map((o) => (
                <div key={o.city}>
                  <div className="font-serif text-2xl">{o.city}</div>
                  <div className="text-xs mt-1 text-[#66635C] tracking-wide uppercase">{o.role}</div>
                  <div className="text-sm mt-3 text-[#A19D94] leading-relaxed">{o.address}</div>
                </div>
              ))}
            </div>
            <div className="mt-10">
              <div className="eyebrow mb-4">On-ground partners</div>
              <ul className="space-y-2 text-sm text-[#A19D94]">
                {LOCAL_PARTNERS.map((p) => (
                  <li key={p.name} className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 bg-[#C84C35]" />
                    <span>{p.name}</span>
                  </li>
                ))}
              </ul>
              <div className="chapter-num text-[#66635C] mt-4">Formal representation via registered Emirati advocacy partners.</div>
            </div>
          </div>
        </div>

        <div className="mt-24 relative">
          <div className="marquee-mask">
            <div className="marquee-track font-serif text-[7vw] leading-none whitespace-nowrap text-[#0E0E0E] tracking-tighter">
              <span>{FIRM.short} · Rane Pingle Law Chambers · UAE · India · USA · </span>
              <span>{FIRM.short} · Rane Pingle Law Chambers · UAE · India · USA · </span>
            </div>
          </div>
          {/* Larger watermark logo */}
          <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none">
            <img src="/media/brand/rplc-logo.svg" alt="" className="rplc-logo-watermark" style={{ height: "320px", width: "auto" }} />
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-[#141414]">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 pb-8 border-b border-[#141414] items-start">
            <div className="lg:col-span-4">
              <div className="eyebrow mb-2">Legal entity</div>
              <div className="font-serif text-xl text-[#F4F1EB] leading-tight">{FIRM.legalEntity}</div>
              <div className="chapter-num text-[#66635C] mt-2">Brand: {FIRM.name}</div>
            </div>
            <div className="lg:col-span-5">
              <div className="eyebrow mb-2">Registered office</div>
              <div className="text-[#A19D94] leading-relaxed">RAKEZ Business Zone, Al Nakheel Area,<br />P.O. Box No. 10055, Ras Al Khaimah, UAE</div>
              <div className="chapter-num text-[#66635C] mt-3">On ground · Boulevard Plaza, Level 3, Downtown Dubai</div>
            </div>
            <div className="lg:col-span-3">
              <div className="eyebrow mb-2">Online</div>
              <div className="text-[#A19D94] flex flex-col items-start gap-1">
                <a href="https://www.rplcuae.com" className="link-hover" target="_blank" rel="noreferrer">www.rplcuae.com</a>
                <a href="https://www.rplawchambers.com" className="link-hover" target="_blank" rel="noreferrer">www.rplawchambers.com</a>
              </div>
            </div>
          </div>
          <div className="pt-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="text-xs text-[#66635C] max-w-3xl leading-relaxed">
              {t.footer.disclaimer} © {new Date().getFullYear()} {FIRM.legalEntity}. Regulated activities and formal representation before UAE authorities are undertaken, where required, through appropriately licensed professionals and collaboration partners.
            </div>
            <div className="flex items-center gap-6 max-w-full">
              <div className="flex flex-wrap gap-x-6 gap-y-3 chapter-num">
                <a href="#" className="link-hover">LinkedIn</a>
                <a href="#" className="link-hover">Instagram</a>
                <Link to="/careers" className="link-hover">Careers</Link>
                <Link to="/privacy" className="link-hover">Privacy</Link>
              </div>
              <div className="hidden lg:block pl-6 border-l border-[#141414]">
                <Logo height={26} testId="footer-logo" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
